import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, ArrowRight } from "lucide-react";
import { Logo } from "./Logo";
import { NAV_LINKS } from "../data/content";

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <header
      data-testid="main-navbar"
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${
        scrolled
          ? "backdrop-blur-xl bg-[#020817]/80 border-b border-white/10"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <nav className="max-w-7xl mx-auto px-6 md:px-12 h-20 flex items-center justify-between">
        <Link to="/" data-testid="nav-logo-link" className="shrink-0">
          <Logo markSize={32} />
        </Link>

        <div className="hidden lg:flex items-center gap-9">
          {NAV_LINKS.map((link) => {
            const active = location.pathname === link.to;
            return (
              <Link
                key={link.to}
                to={link.to}
                data-testid={`nav-link-${link.label.toLowerCase().replace(/\s/g, "-")}`}
                className={`text-sm tracking-wide transition-colors duration-200 ${
                  active ? "text-white" : "text-[#94A3B8] hover:text-white"
                }`}
              >
                {link.label}
              </Link>
            );
          })}
        </div>

        <div className="hidden lg:block">
          <button
            data-testid="nav-cta-button"
            onClick={() => navigate("/contact")}
            className="group inline-flex items-center gap-2 bg-[#4FC3F7] text-[#020817] hover:bg-[#00E5FF] transition-colors duration-300 px-6 py-3 text-sm font-semibold"
          >
            Request Demo
            <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" strokeWidth={2} />
          </button>
        </div>

        <button
          data-testid="mobile-menu-toggle"
          className="lg:hidden text-white"
          onClick={() => setOpen((v) => !v)}
          aria-label="Toggle menu"
        >
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </nav>

      {open && (
        <div
          data-testid="mobile-menu"
          className="lg:hidden bg-[#020817]/95 backdrop-blur-xl border-t border-white/10 px-6 py-6 flex flex-col gap-4 animate-fade-in"
        >
          {NAV_LINKS.map((link) => (
            <Link
              key={link.to}
              to={link.to}
              data-testid={`mobile-nav-link-${link.label.toLowerCase().replace(/\s/g, "-")}`}
              className="text-base text-[#94A3B8] hover:text-white transition-colors py-1"
            >
              {link.label}
            </Link>
          ))}
          <button
            data-testid="mobile-nav-cta"
            onClick={() => navigate("/contact")}
            className="mt-2 inline-flex items-center justify-center gap-2 bg-[#4FC3F7] text-[#020817] px-6 py-3 text-sm font-semibold"
          >
            Request Demo <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      )}
    </header>
  );
};
